import { z } from "zod";
//#region src/kernel/authentication.ts
/**
* The authentication method vocabulary of a session, and the second-factor test that the guards of
* both planes share (kernel apps, WS-1b, decision 5, R-042).
*
* A login writes the `amr` claim (RFC 8176) and the `auth_time` claim on each token of its session
* family, and a refresh copies both. cp-auth-api (WS-6) and core-api (WS-10, WS-11, WS-19) read
* them with the names of this module, so the two planes count factors the same way.
*
* Browser-safe: no Node builtin and no Node global. No schema uses `.transform()`.
*/
/**
* The four authentication methods, in this order:
*
* - `pwd`: a password (RFC 8176);
* - `fed`: an identity provider login (a platform value);
* - `rec`: a recovery code (a platform value);
* - `pop`: a passkey assertion with user verification (RFC 8176, proof of possession).
*/
const AUTHENTICATION_METHODS = [
	"pwd",
	"fed",
	"rec",
	"pop"
];
const authenticationMethodSchema = z.enum(AUTHENTICATION_METHODS);
/**
* The `amr` claim: 1 to 4 methods with no duplicate, in any order. It refuses an empty list, an
* unknown method and a repeated method. A verifier that reads a token parses the claim with this
* schema, and drops a claim that fails (index defaults row "a claim is absent when its check did
* not pass").
*/
const authenticationMethodListSchema = z.array(authenticationMethodSchema).min(1).max(AUTHENTICATION_METHODS.length).superRefine((methods, ctx) => {
	if (new Set(methods).size !== methods.length) ctx.addIssue({
		code: "custom",
		message: "an authentication method occurs twice"
	});
});
/**
* The `auth_time` claim: the epoch second of the login that started the session family. A
* non-negative safe integer. It refuses a fraction, a negative number and a value past
* `Number.MAX_SAFE_INTEGER`.
*/
const authTimeSchema = z.int().min(0);
/**
* True when the session passed the second factor: `amr` holds `pop`. A passkey login (`['pop']`)
* counts as both factors. A password, an identity provider login or a recovery session (`['rec']`,
* I-8) alone counts as none. An absent claim answers `false` (refuse over accept). Never throws.
* Pure.
*/
function isSecondFactorSatisfied(amr) {
	return amr !== void 0 && amr.includes("pop");
}
//#endregion
export { AUTHENTICATION_METHODS, authTimeSchema, authenticationMethodListSchema, isSecondFactorSatisfied };

//# sourceMappingURL=authentication.js.map